/* v3-cart.js: bag drawer over the AJAX Cart API + Section Rendering API.
   Exposes window.v3.cart = { add(formData|items), change(line, qty), update(attrs), setAttributes(attrs),
   updateNote(note), setNote(note), refresh(), register(sectionId), open() }; each mutating call re-renders the drawer section and updates every
   [data-v3-cart-count]. Intercepts every /cart/add form on the page and [data-v3-quick-add] buttons. Network failures
   fall back to native behaviour; 4xx errors are shown inline.
   v3.cart.refresh() re-renders after an add made by someone else (the options app, see wireAppCart in v3-product.js).
   Other sections that show the cart (the cart page, sections/v3-main-cart.liquid) register their section id: every
   request then asks the Section Rendering API for them too, and the v3:cart:updated event carries the rendered HTML
   in detail.sections so each one swaps its own markup (18 Sep 2026). A section whose script runs before this one
   (this file loads from the overlay group at the end of the body) pushes its id onto v3.cartSections instead.
   The drawer node is looked up on every use (never cached): the theme editor re-renders the section in place.
   The gift note (drawer, cart page, product page field) is the cart note, never an attribute: Shopify copies it into
   the order's Notes box, which the kitchen print sheets, packing slips, Order Printer and StarShipit read (14 Oct 2026;
   a gift-note attribute from 26 Sep printed blank). A cart still holding that attribute is moved over once on load.
   Requires v3-core.js (loaded from <head>): v3.root, v3.parseFragment, v3.debounce, v3.open. */
(function () {
  'use strict';
  var doc = document;
  var v3 = window.v3 = window.v3 || {};
  if (v3.cart) return; /* the theme editor re-runs section scripts; wire once */
  function drawerEl() { return doc.querySelector('[data-v3-cart]'); }
  var first = drawerEl(); if (!first) return;
  var SECTION_ID = first.getAttribute('data-section-id'); /* stable across re-renders of the same section */
  var root = v3.root;
  var extra = v3.cartSections = v3.cartSections || []; /* section ids registered by other cart views (the cart page) */
  function sectionIds() { return [SECTION_ID].concat(extra).join(','); }

  function sectionsUrl() { return window.location.pathname + window.location.search; }
  function contents() { var d = drawerEl(); return d && d.querySelector('[data-v3-cart-contents]'); }
  function updateCounts(count) {
    var els = doc.querySelectorAll('[data-v3-cart-count]');
    for (var i = 0; i < els.length; i++) els[i].textContent = count;
    /* header trolley (16 Sep 2026): the count inside the basket is hidden by CSS when the wrapper's data-count is 0 */
    var wraps = doc.querySelectorAll('[data-v3-cart-indicator]');
    for (var j = 0; j < wraps.length; j++) wraps[j].setAttribute('data-count', count == null ? '0' : String(count));
  }
  /* html: the drawer section's render; sections: every section the response carried (drawer plus registered) */
  function applySection(html, sections) {
    var fresh = html && v3.parseFragment(html, '[data-v3-cart-contents]');
    var live = contents();
    if (fresh && live) {
      live.innerHTML = fresh.innerHTML;
      live.setAttribute('data-count', fresh.getAttribute('data-count'));
      updateCounts(fresh.getAttribute('data-count'));
    }
    doc.dispatchEvent(new CustomEvent('v3:cart:updated', { bubbles: true, detail: { sections: sections || {} } }));
  }
  /* busy state per control: the drawer dims while any request is in flight; a line marks itself so a second tap on
     the same line waits, while another line (or a quick add) can still go */
  var inflight = 0;
  function setBusy(el, state) {
    var d = drawerEl();
    inflight = Math.max(0, inflight + (state ? 1 : -1));
    if (d) d.classList.toggle('is-busy', inflight > 0);
    if (el) { if (state) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy'); }
  }
  function isBusy(el) { return !!el && el.getAttribute('aria-busy') === 'true'; }
  function showError(message) {
    var live = contents(); if (!live) return;
    var el = live.querySelector('.v3-cart__error');
    if (!el) { el = doc.createElement('p'); el.className = 'v3-cart__error'; el.setAttribute('role', 'alert'); live.insertBefore(el, live.firstChild); }
    el.textContent = message;
  }
  function post(path, body, isForm) {
    return fetch(root + path, {
      method: 'POST',
      headers: isForm ? { 'Accept': 'application/json' } : { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: isForm ? body : JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok) { var err = new Error(data.description || data.message || ('Request failed (' + r.status + ')')); err.status = r.status; err.data = data; throw err; }
        return data;
      });
    });
  }
  function withSection(body) { body.sections = sectionIds(); body.sections_url = sectionsUrl(); return body; }
  function updateRendered(body) {
    return post('cart/update.js', withSection(body)).then(function (d) {
      if (d.sections && d.sections[SECTION_ID]) applySection(d.sections[SECTION_ID], d.sections);
      return d;
    });
  }
  function resync() {
    return fetch(sectionsUrl() + (sectionsUrl().indexOf('?') > -1 ? '&' : '?') + 'sections=' + encodeURIComponent(sectionIds()))
      .then(function (r) { return r.json(); })
      .then(function (d) { if (d && d[SECTION_ID]) applySection(d[SECTION_ID], d); });
  }

  v3.cart = {
    /** Add from a FormData (product form) or an items array. Resolves with the /cart/add.js response. */
    add: function (payload) {
      var isForm = payload instanceof FormData;
      if (isForm) { payload.append('sections', sectionIds()); payload.append('sections_url', sectionsUrl()); }
      else payload = withSection({ items: payload });
      return post('cart/add.js', payload, isForm).then(function (d) {
        if (d.sections && d.sections[SECTION_ID]) applySection(d.sections[SECTION_ID], d.sections);
        return d;
      });
    },
    change: function (line, quantity) {
      return post('cart/change.js', withSection({ line: line, quantity: quantity })).then(function (d) {
        if (d.sections && d.sections[SECTION_ID]) applySection(d.sections[SECTION_ID], d.sections);
        return d;
      });
    },
    update: function (attributes) { return updateRendered({ attributes: attributes }); },
    /** Save cart attributes without re-rendering anything (a field that must keep focus). */
    setAttributes: function (attributes) { return post('cart/update.js', { attributes: attributes }); },
    /** Save the gift note (the cart note) and re-render. */
    updateNote: function (note) { return updateRendered({ note: note }); },
    /** Save the gift note while it is typed, without re-rendering (the textarea keeps focus). */
    setNote: function (note) { return post('cart/update.js', { note: note }); },
    /** Re-render the drawer (and every registered section) from the server, for adds made outside this script. */
    refresh: function () { return resync(); },
    /** Another section that shows the cart: rendered with every request from now on (see the header comment). */
    register: function (sectionId) { if (sectionId && extra.indexOf(sectionId) < 0) extra.push(sectionId); },
    open: function () { if (v3.open) v3.open('cart'); }
  };

  function changeLine(item, line, qty) {
    if (isBusy(item)) return; setBusy(item, true);
    v3.cart.change(line, qty)
      .catch(function (err) {
        /* resync from the server rather than bouncing the customer to /cart */
        return resync().then(function () { showError(err.message); }).catch(function () { window.location.href = root + 'cart'; });
      })
      .finally(function () { setBusy(item.isConnected ? item : null, false); });
  }

  /* delegated, on the document: the drawer node is replaced by editor re-renders. The stepper buttons are the
     shared v3-qty snippet, which v3-core.js clamps and turns into a change event on the input */
  doc.addEventListener('click', function (e) {
    var item = e.target.closest('[data-v3-cart] .v3-cart__item'); if (!item) return;
    if (e.target.closest('[data-v3-line-remove]')) changeLine(item, Number(item.getAttribute('data-line')), 0);
  });
  doc.addEventListener('change', function (e) {
    var input = e.target.closest('[data-v3-cart] [data-v3-line-qty]'); if (!input) return;
    var item = input.closest('.v3-cart__item');
    changeLine(item, Number(item.getAttribute('data-line')), Math.max(0, Number(input.value) || 0));
  });

  /* gift note in the drawer: save while typing (no re-render, the textarea keeps focus) */
  var saveNote = v3.debounce(function (value) {
    v3.cart.setNote(value).catch(function () { /* re-saves on next input */ });
  }, 600);
  /* a cart from 26 Sep to 14 Oct 2026 may hold the note as a gift-note attribute (the drawer renders
     data-v3-legacy-note only when the cart note is empty): move it into the note and clear the attribute, once */
  var legacyNote = first.getAttribute('data-v3-legacy-note');
  if (legacyNote) post('cart/update.js', { note: legacyNote, attributes: { 'gift-note': '' } }).catch(function () { /* tries again on the next page */ });
  doc.addEventListener('input', function (e) {
    var ta = e.target.closest('[data-v3-cart] [data-v3-gift-note]'); if (!ta) return;
    saveNote(ta.value);
  });

  /* quick add buttons: [data-v3-quick-add="<variant id>"] (where a nested form is not allowed) */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-v3-quick-add]'); if (!btn) return;
    e.preventDefault();
    if (isBusy(btn)) return; setBusy(btn, true);
    v3.cart.add([{ id: Number(btn.getAttribute('data-v3-quick-add')), quantity: 1 }])
      .then(function () { v3.cart.open(); })
      .catch(function (err) { if (err.status === 422) { v3.cart.open(); showError(err.message); } else if (btn.href) { window.location.href = btn.href; } })
      .finally(function () { setBusy(btn.isConnected ? btn : null, false); });
  });

  /* AJAX add: intercept every /cart/add form on the page */
  doc.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    e.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;
    /* form-associated controls (form="…") are in form.elements even when they sit outside the form */
    var btn = Array.prototype.find.call(form.elements, function (el) { return el.type === 'submit' || el.name === 'add'; }) || null;
    form.setAttribute('aria-busy', 'true');
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    v3.cart.add(new FormData(form))
      .then(function (d) {
        v3.cart.open();
        form.dispatchEvent(new CustomEvent('v3:cart:added', { bubbles: true, detail: d }));
      })
      .catch(function (err) {
        /* any 4xx carries Shopify's JSON message (422 sold out or over quantity, 404 variant): show it inline */
        if (err.status >= 400 && err.status < 500) {
          var d = drawerEl();
          if (d && d.contains(form)) showError(err.message); /* the drawer's own upsell form has no listener of its own */
          form.dispatchEvent(new CustomEvent('v3:cart:error', { bubbles: true, detail: err }));
        }
        else { form.submit(); /* network failure or 5xx: never trap the customer */ }
      })
      .finally(function () { form.removeAttribute('aria-busy'); if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; } });
  });
})();
